/**
 * Forecast Service
 *
 * API endpoints for investment forecast operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';

export interface MonthlyForecastEntry {
  month: number;
  calendar_month: string;
  costs: {
    total: number;
    capex: number;
    opex: number;
    by_type?: Record<string, number>;
  };
  benefits: {
    total: number;
    capex_savings: number;
    opex_savings: number;
    revenue_increase: number;
    by_category?: Record<string, number>;
  };
  net_value: number;
  cumulative_net: number;
}

export interface ForecastTotals {
  total_cost: number;
  total_benefit: number;
  net_benefit: number;
  roi_percentage: number | null;
  payback_month: number | null;
  npv: number | null;
}

export interface ForecastResponse {
  investment_id: number;
  months: MonthlyForecastEntry[];
  totals: ForecastTotals;
  message?: string;
}

export interface GenerateForecastOptions {
  start_date?: string;
  discount_rate?: number;
}

/**
 * Forecast Service
 */
class ForecastService {
  /**
   * Get forecast for an investment
   */
  async get(investmentId: number): Promise<ApiResponse<ForecastResponse>> {
    return apiClient.get<ForecastResponse>(`/investments/${investmentId}/forecast`);
  }

  /**
   * Generate/regenerate forecast for an investment
   */
  async generate(
    investmentId: number,
    options?: GenerateForecastOptions
  ): Promise<ApiResponse<ForecastResponse>> {
    return apiClient.post<ForecastResponse>(`/investments/${investmentId}/forecast`, options);
  }

  /**
   * Delete forecast for an investment
   */
  async delete(
    investmentId: number
  ): Promise<ApiResponse<{ message: string; deleted_count: number }>> {
    return apiClient.delete(`/investments/${investmentId}/forecast`);
  }
}

export const forecastService = new ForecastService();
